import { trackingApi, type TrackingConfig } from "@/lib/api";
import { pixelEnabledOn } from "@/lib/meta-pixel";

/**
 * The Google tag (gtag.js) for GA4 and Google Ads (DEL-435).
 *
 * The IDs come from the API, not the bundle: the storefront is prerendered,
 * and the agency hands them over after launch. Nothing loads until an ID is
 * configured, and nothing loads off the live storefront, the same rule as the
 * Pixel.
 *
 * Events queue in dataLayer from the first call, so one fired before the
 * config arrives is still sent once the tag loads.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let config: Promise<TrackingConfig | null> | null = null;

function gtag(..._args: unknown[]) {
  window.dataLayer = window.dataLayer ?? [];
  // gtag.js reads the arguments object itself, not an array.
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

/** Load the tag once, if there is one to load. Resolves to the config in force. */
export function loadGoogleTag(): Promise<TrackingConfig | null> {
  if (typeof window === "undefined" || !pixelEnabledOn(window.location.hostname)) return Promise.resolve(null);
  config ??= trackingApi
    .config()
    .then((c) => {
      const ids = [c.ga4MeasurementId, c.googleAdsId].filter((id): id is string => Boolean(id));
      const first = ids[0];
      if (!first) return null;
      window.gtag = gtag;
      gtag("js", new Date());
      // Page views are sent per route by the app; the tag's own would count
      // only the first load of a single-page app.
      for (const id of ids) gtag("config", id, { send_page_view: false });
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(first)}`;
      document.head.appendChild(script);
      return c;
    })
    .catch(() => null);
  return config;
}

/** Send one event once the tag is up. Does nothing when no tag is configured. */
export function googleEvent(name: string, params: Record<string, unknown>): void {
  void loadGoogleTag().then((c) => {
    if (c) gtag("event", name, params);
  });
}

/** The Google Ads Purchase conversion, when its label is configured. */
export function googleAdsPurchase(params: { value: number; transactionId: string }): void {
  void loadGoogleTag().then((c) => {
    if (!c?.googleAdsId || !c.googleAdsPurchaseLabel) return;
    gtag("event", "conversion", {
      send_to: `${c.googleAdsId}/${c.googleAdsPurchaseLabel}`,
      value: params.value,
      currency: "IDR",
      transaction_id: params.transactionId,
    });
  });
}

/**
 * GA's ids for this visitor, for the server's copy of the purchase (DEL-440):
 * the client id from `_ga`, and the GA4 session id from `_ga_<stream>`. They
 * let GA4 put a purchase the server reports (a VA buyer who never came back,
 * say) on the visit and the ad click that started it.
 *
 * Both cookie formats are read: `GS1.1.<session>.…` and the newer
 * `GS2.1.s<session>$o…`. Pure, for the tests.
 */
export function gaIds(cookie: string, measurementId: string): { clientId?: string; sessionId?: string } {
  const jar = new Map(
    cookie
      .split(";")
      .map((c) => c.trim())
      .filter(Boolean)
      .map((c) => [c.slice(0, c.indexOf("=")), c.slice(c.indexOf("=") + 1)] as const),
  );
  // GA1.1.1234567890.1700000000 → 1234567890.1700000000
  const client = jar.get("_ga")?.split(".");
  const clientId = client && client.length >= 4 ? client.slice(-2).join(".") : undefined;

  const stream = jar.get(`_ga_${measurementId.replace(/^G-/, "")}`) ?? "";
  const sessionId = stream.startsWith("GS2.")
    ? /(?:^|\$|\.)s(\d+)/.exec(stream.slice(4))?.[1]
    : stream.startsWith("GS1.")
      ? stream.split(".")[2]
      : undefined;
  return { clientId, sessionId: sessionId && /^\d+$/.test(sessionId) ? sessionId : undefined };
}

/** GA's ids for checkout to send, or nothing when no GA4 stream is configured. */
export async function gaIdsForCheckout(): Promise<{ clientId?: string; sessionId?: string } | undefined> {
  const c = await loadGoogleTag();
  if (!c?.ga4MeasurementId || typeof document === "undefined") return undefined;
  const ids = gaIds(document.cookie, c.ga4MeasurementId);
  return ids.clientId ? ids : undefined;
}
